import { CreateRoleDto, QueryFactory } from '@lmt/shared-types';

import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { permissionDisplayFilter } from '@lmt/unicorn-shared/app/components/roles/list/components/RolesTable';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import {
  convertToErrorString,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { permissionsMappings } from '@lmt/unicorn-shared/app/api/auth/token-decoder';
import { localizePermission } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import localStyles from './RoleForm.module.scss';
import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { permissionDependencies } from './types';
import { useTranslation } from 'react-i18next';
import { snakeCase } from 'lodash';

interface PermissionCheckBoxProps {
  permission: string;
}

const nameof = nameofFactory<CreateRoleDto>();

export interface RoleProps {
  roleId?: number;
}

export const RoleForm: FC<FormComponentProps & RoleProps> = (props) => {
  const { formType, formId, tryCompleteForm, roleId, loadBlocking } = props;
  const i18next = useScopedTranslation('roles.form');
  const i18n = useTranslation();
  const modals = useModal();
  const [allPermissions, setAllPermissions] = useState<string[]>(
    Object.values(permissionsMappings),
  );
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [isDefault, setDefault] = useState<boolean>(!!props.roleId);

  const {
    register,
    reset,
    handleSubmit,
    formState,
    setError,
    setValue,
    watch,
  } = useForm<CreateRoleDto>({
    shouldFocusError: true,
    criteriaMode: 'all',
  });

  useEffect(() => {
    async function getData() {
      const rolesRequestResult = await QueryFactory.RoleClient.getPermissions();
      setAllPermissions(rolesRequestResult);
      if (roleId) {
        const roleRequest = await QueryFactory.RoleClient.getRole(roleId);
        if (!roleRequest.isDefault && roleRequest.isEditable) {
          setDefault(false);
        }
        if (roleRequest.permissions) {
          setRolePermissions(roleRequest.permissions);
        }
        setValue('name', roleRequest.name);
      } else {
        setDefault(false);
      }
    }

    loadBlocking(formId, i18next.t('loading'), () => getData());
  }, []);

  const permissionSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newElement = allPermissions.find((x) => x === event.target.name);
        if (newElement) {
          setRolePermissions([...rolePermissions, newElement!]);
        }
      } else {
        const dependentPermissions = permissionDependencies
          .filter((x) => x.mainPermission === event.target.name)
          .map((x) => x.dependentPermission);
        setRolePermissions(
          rolePermissions.filter(
            (x) => x !== event.target.name && !dependentPermissions.includes(x),
          ),
        );
      }
    },
    [allPermissions, rolePermissions],
  );

  const isDisabled = useCallback(
    (permission: string) => {
      const mainPermissions = permissionDependencies
        .filter((x) => x.dependentPermission === permission)
        .map((x) => x.mainPermission);
      if (mainPermissions.some((x) => !rolePermissions.includes(x))) {
        return true;
      }
      return false;
    },
    [rolePermissions],
  );

  const submitForm = useErrorHandler<CreateRoleDto>(
    useCallback(
      async (data: CreateRoleDto) => {
        data.permissions = rolePermissions;
        await tryCompleteForm(formType, formId, async () => {
          try {
            if (roleId) {
              await QueryFactory.RoleClient.editRole(roleId, data);
            } else {
              await QueryFactory.RoleClient.createRole(data);
            }
          } catch (err) {
            await modals.showError({ text: convertToErrorString(err) });
          }
        });
      },
      [rolePermissions, formType, formId, tryCompleteForm, modals],
    ),
    setError,
    reset,
  );

  const PermissionCheckbox = useCallback(
    (props: PermissionCheckBoxProps) => {
      const permission = props.permission;
      return (
        <CheckBox
          title={localizePermission(permission)}
          name={permission}
          onChange={permissionSelect}
          checked={rolePermissions.includes(permission)}
          className={localStyles.checkbox}
          testId={`RoleFormPermission-${permission}`}
          disabled={isDisabled(permission) || isDefault}
        />
      );
    },
    [rolePermissions, isDefault],
  );

  const roleName = watch('name');

  return (
    <form
      className={localStyles.container}
      onSubmit={handleSubmit(submitForm.handler)}
    >
      <div className={styles.dataContainer}>
        <Field title={i18next.t('name')} required={!isDefault}>
          {isDefault ? (
            <Input
              value={i18n.t(
                `roles.list.default_roles.${snakeCase(roleName)}`,
                '',
              )}
              errorText={formState.errors.name?.message}
              disabled
            />
          ) : (
            <Input
              {...register(nameof('name'), requiredRule())}
              errorText={formState.errors.name?.message}
              autoFocus={true}
            />
          )}
        </Field>
        <Field className={styles.field} title={i18next.t('permissions')}>
          {!isDefault && (
            <div className={localStyles.optionsContainer}>
              <UniLink
                color={ButtonColor.Secondary}
                title={i18next.t('check_all')}
                className={localStyles.link}
                disabled={allPermissions.length === rolePermissions.length}
                onClick={() =>
                  setRolePermissions(
                    allPermissions.filter(
                      (x) => !permissionDisplayFilter.includes(x),
                    ),
                  )
                }
              />
              <UniLink
                color={ButtonColor.Secondary}
                title={i18next.t('check_none')}
                className={localStyles.link}
                disabled={rolePermissions.length === 0}
                onClick={() => setRolePermissions([])}
              />
            </div>
          )}
          <div className={localStyles.firstRow}>
            <div className={styles.column}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.tenant')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.tenantSettingsChange}
              />
              <PermissionCheckbox
                permission={permissionsMappings.createSubtenant}
              />
              <PermissionCheckbox
                permission={permissionsMappings.deleteSubtenants}
              />
              <PermissionCheckbox permission={permissionsMappings.auditLog} />
            </div>
            <div className={styles.column}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.users_and_roles')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.userManagement}
              />
              <PermissionCheckbox
                permission={permissionsMappings.resetPassword}
              />
              <PermissionCheckbox
                permission={permissionsMappings.roleManagement}
              />
              <PermissionCheckbox
                permission={permissionsMappings.passwordValidationSettings}
              />
            </div>
            <div className={styles.column}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.hl7_and_gdt')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.hl7Management}
              />
              <PermissionCheckbox permission={permissionsMappings.hl7} />
              <PermissionCheckbox
                permission={permissionsMappings.hl7Transporter}
              />
              <PermissionCheckbox
                permission={permissionsMappings.gdtManagement}
              />
              <PermissionCheckbox permission={permissionsMappings.gdt} />
              <PermissionCheckbox
                permission={permissionsMappings.gdtTransporter}
              />
            </div>
          </div>
          <div className={localStyles.row}>
            <div className={styles.column}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.patients')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.editExternalPatientNumber}
              />
              <PermissionCheckbox
                permission={permissionsMappings.editExternalVisitNumber}
              />
              <PermissionCheckbox
                permission={permissionsMappings.patientDeleteAndRestore}
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.patientAnonymizeAndDeletePermanently
                }
              />
              <PermissionCheckbox
                permission={permissionsMappings.mergePatients}
              />
              <PermissionCheckbox
                permission={permissionsMappings.movePatientsToAnotherSubtenant}
              />
              <PermissionCheckbox
                permission={permissionsMappings.deleteCases}
              />
              <PermissionCheckbox permission={permissionsMappings.reopenCase} />
              <PermissionCheckbox
                permission={permissionsMappings.deleteVisits}
              />
            </div>
            <div className={styles.column}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.documents')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.documentTemplateManagement}
              />
              <PermissionCheckbox
                permission={permissionsMappings.createOrEditDocuments}
              />
              <PermissionCheckbox
                permission={permissionsMappings.sendDocumentByEmail}
              />
              <PermissionCheckbox
                permission={permissionsMappings.deleteDocuments}
              />
              <PermissionCheckbox
                permission={permissionsMappings.statisticsDesigner}
              />
              <PermissionCheckbox
                permission={permissionsMappings.statisticsReport}
              />
            </div>
            <div className={styles.column}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.measurements')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.editDataBlockTypesAndFields}
              />
              <PermissionCheckbox
                permission={permissionsMappings.editMeasurementMappings}
              />
              <PermissionCheckbox
                permission={permissionsMappings.reImportAllMeasurements}
              />
              <PermissionCheckbox
                permission={permissionsMappings.importMeasurements}
              />
              <PermissionCheckbox
                permission={permissionsMappings.manualMeasurementImporting}
              />
              <PermissionCheckbox
                permission={permissionsMappings.dataBlockManagement}
              />
              <PermissionCheckbox
                permission={permissionsMappings.inputTemplateManagement}
              />
            </div>
          </div>
          <div className={localStyles.row}>
            <div className={styles.column} style={{ width: '100%' }}>
              <div className={localStyles.title}>
                {i18next.t('permission_group_titles.ui')}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.configureMeasurementUI}
              />
              <PermissionCheckbox
                permission={permissionsMappings.calendarSettingsManagement}
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.manageSharedCalendarConfigurations
                }
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.editPersonalDashboardUIConfiguration
                }
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.editTenantDashboardUIConfiguration
                }
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.editPersonalPatientOverviewUIConfiguration
                }
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.editTenantPatientOverviewUIConfiguration
                }
              />
              <PermissionCheckbox
                permission={
                  permissionsMappings.manageSharedPatientListConfigurations
                }
              />
            </div>
            <div className={styles.column} style={{ width: '50%' }}>
              <div className={localStyles.title}>
                {i18next.t(
                  'permission_group_titles.dictionaries_and_resources',
                )}
              </div>
              <PermissionCheckbox
                permission={permissionsMappings.dictionariesManagement}
              />
              <PermissionCheckbox
                permission={permissionsMappings.deleteDictionaries}
              />
              <PermissionCheckbox
                permission={permissionsMappings.doctorManagement}
              />
              <PermissionCheckbox
                permission={permissionsMappings.manageResources}
              />
              <PermissionCheckbox
                permission={permissionsMappings.manageAbsences}
              />
              <PermissionCheckbox
                permission={permissionsMappings.manageNotes}
              />
            </div>
          </div>
        </Field>
      </div>
      {!isDefault && (
        <div className={styles.submitContainer}>
          <SubmitButton
            submitting={formState.isSubmitting}
            className={styles.submit}
            title={
              roleId ? i18next.t('save_button') : i18next.t('create_button')
            }
          />
        </div>
      )}
    </form>
  );
};
